import React, { useState } from 'react';
import { useSpaceProfile } from './useSpaceProfile';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  ImageList,
  ImageListItem,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const Image = ({
  src,
  alt,
  style = {},
}: {
  src: string;
  alt: string;
  style?: React.CSSProperties;
}) => (
  <ImageListItem sx={{ aspectRatio: '4 / 3' }}>
    <img
      src={src}
      alt={alt}
      style={{
        borderRadius: '12px',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
        ...style,
      }}
    />
  </ImageListItem>
);

export const ImageGallery = () => {
  const [open, setOpen] = useState(false);
  const { space, venue } = useSpaceProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: 'white',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'grey.50',
            },
            minWidth: 'auto',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          Show All Photos
        </Button>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 3,
            px: 4,
            pt: 4,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {space.name} at {venue.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4, overflow: 'auto' }}>
          <Stack spacing={3} sx={{ pt: 3 }}>
            {/* Space Images Section */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {space.name}
              </Typography>
              <ImageList
                cols={isMobile ? 1 : 2}
                gap={16}
                sx={{
                  m: 0,
                  '& .MuiImageListItem-root': {
                    borderRadius: '12px',
                    overflow: 'hidden',
                  },
                }}
              >
                {space.images.map((image) => (
                  <Image key={image.url} src={image.url} alt={image.url} />
                ))}
              </ImageList>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Venue Images Section */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {venue.name}
              </Typography>
              <ImageList
                cols={isMobile ? 1 : 2}
                gap={16}
                sx={{
                  m: 0,
                  '& .MuiImageListItem-root': {
                    borderRadius: '12px',
                    overflow: 'hidden',
                  },
                }}
              >
                {venue.images.map((image) => (
                  <Image key={image.url} src={image.url} alt={image.url} />
                ))}
              </ImageList>
            </Box>

            {/* Menu Items Section */}
            {venue.menu.previewMenuItems?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    Event Menu
                  </Typography>
                  <ImageList
                    cols={isMobile ? 2 : 4}
                    gap={16}
                    sx={{
                      m: 0,
                      '& .MuiImageListItem-root': {
                        borderRadius: '12px',
                        overflow: 'hidden',
                      },
                    }}
                  >
                    {venue.menu.previewMenuItems.map((item) => (
                      <Image
                        key={item.id}
                        src={item.image}
                        alt={item.name}
                        style={{
                          objectFit: 'cover',
                        }}
                      />
                    ))}
                  </ImageList>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
